import React, { Fragment, useState, useEffect } from "react"
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  CardBody,
} from "reactstrap"
import { useMutation } from "react-query"
import { GENERAL } from "../../api/general"
import { useNavigate } from "react-router-dom"
import { useAsyncDebounce } from "react-table"
//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb"
//i18n
import { withTranslation } from "react-i18next"
import Swal from "sweetalert2"
import PaginatedDataTable from "components/PaginatedDataTable"
import { getAuthUser, getUserRole, ROLES } from "constants/roles"

const ThresholdManagement = props => {
  const navigate = useNavigate()
  const authUser = getAuthUser() || {}
  const currentUserRole = getUserRole()
  const willRedirectToLgaThreshold =
    currentUserRole === ROLES.STATE_ADMIN && !!authUser.state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [filter, setFilter] = useState("")

  const [value, setValue] = React.useState("")
  const onChange = useAsyncDebounce(value => {
    setFiilter(value || undefined)
  }, 200)

  const fetchRecords = async (page, pageSize, filter) => {
    Swal.fire({
      title: "Fetching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })

    try {
      const response = await getStatesThreshold.mutateAsync({
        page,
        pageSize,
        searchKey: filter ?? "",
      })
      Swal.close()
      setData(response.palliative)
      setTotalRecords(response.totalRecords)
      console.log("totalrecords", response.totalRecords)
    } catch (error) {
      Swal.fire({
        title: "Error fetching records",
        text: error.message,
        icon: "error",
      })
    }
  }

  useEffect(() => {
    if (willRedirectToLgaThreshold) {
      navigate("/threshold-management-lga", {
        state: { id: authUser.state, name: authUser.stateName },
        replace: true,
      })
    }
  }, [])

  useEffect(() => {
    if (willRedirectToLgaThreshold) return
    fetchRecords(page, pageSize, filter)
  }, [page, pageSize])

  const getStatesThreshold = useMutation({
    mutationFn: async ({ page, pageSize, searchKey }) => {
      return GENERAL.getStatesThreshold({ page, pageSize, searchKey })
    },
    onError: error => {
      Swal.fire("Error fetching records", error.message, "error")
    },
  })

  const handlePageChange = page => {
    setPage(page)
  }

  const handlePerRowsChange = (newPageSize, page) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page whenever page size changes
  }

  const startIndex = (page - 1) * pageSize;

  const handleSearchFn = async () => {
    Swal.fire({
      title: "Searching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    setPage(1) // Reset to first page when searching
    await fetchRecords(1, pageSize, filter.trim())
  }

  //meta title
  document.title = "Ward Registrars Management System"

  const columns = [
    {
      name: "#",
      sortable: true,
      selector: (row, index) => startIndex + index + 1,
      width: "4rem",
    },
    {
      name: "State",
      selector: row => row.name,
      sortable: true,
    },
    {
      name: "Threshold",
      sortable: true,
      selector: row => row.threshold,
    },
    {
      name: "Total Applications",
      sortable: true,
      selector: row => row.totalApplications,
    },
    {
      name: "Successful Applications",
      sortable: true,
      selector: row => row.successfulApplications,
    },
    {
      name: "Unsuccessful Applications",
      sortable: true,
      selector: row => row.unSuccessfulApplications,
    },
    {
      name: "",
      disableFilters: true,
      cell: row => (
        <Button
          type="button"
          color="primary"
          className="btn-sm btn-rounded"
          onClick={() => gotoLga(row)}
        >
          View LGAs
        </Button>
      ),
    }
  ]

  const gotoLga = (row) => {
    const id = row?.id;
    const name = row?.name;
    if (!id) {
      return Swal.fire("Process failed", "LGA not found", "error");
    }
    navigate(`/threshold-management-lga`, { state: { id, name } });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Row>
            <Breadcrumbs
              title={props.t("Threshold Management")}
              breadcrumbItem={props.t("Threshold Management")}
              hasBackBtn={true}
            />
          </Row>

          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">All States by Threshold</div>
              <Fragment>
                <Row className="mb-2">
                  <React.Fragment>
                    <Col md={6}>
                      <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                        <div
                          className="position-relative"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <label
                            htmlFor="search-bar-0"
                            className="search-label"
                          >
                            <span id="search-bar-0-label" className="sr-only">
                              Search this table
                            </span>
                            <input
                              onChange={e => setFilter(e.target.value)}
                              id="search-bar-0"
                              type="text"
                              className="form-control"
                              placeholder={`Search table`}
                              value={filter || ""}
                            />
                          </label>
                          <i
                            className="bx bx-search-alt search-icon"
                            style={{ cursor: "pointer" }}
                            onClick={handleSearchFn}
                          ></i>
                          <div
                            className="d-inline-block"
                            style={{ marginLeft: 5, marginBottom: 8 }}
                          >
                            <Button
                              type="button"
                              color="primary"
                              className="btn-md btn-rounded"
                              onClick={handleSearchFn}
                            >
                              Search
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </React.Fragment>
                </Row>

                <PaginatedDataTable
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  handlePageChange={handlePageChange}
                  handlePerRowsChange={handlePerRowsChange}
                />
              </Fragment>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(ThresholdManagement)
